import React from 'react';
import { StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { FontFamily } from '../../../../constants/fonts';
import WesDistributionCard from './components/WesDistributionCard';
import WesGeographyBars from './components/WesGeographyBars';
import WesPressable from './components/WesPressable';
import WesKpiCards, { WES_SCOPE_LABEL } from './components/WesKpiCards';
import { toSectionStatus } from './components/WesSectionState';
import WesTrendChart from './components/WesTrendChart';
import type { WesHierarchyLevel, WesTimeGrain } from './useWesPalletStagingFilters';
import type { WesOverviewSections } from './useWesOverview';
import { WES_TREND_LOCATION_CAP, toGeoBars, toTrend } from './wesPalletStagingMappers';
import type { WesStagingScope } from './wesPalletStagingStaticRows';

const SCOPES: WesStagingScope[] = ['all', 'audit', 'other'];

const SCOPE_NOTE: Record<WesStagingScope, string> = {
  all: 'Staged counts audit-related + other staging',
  audit: 'Staged counts audit-related staging only',
  other: 'Staged counts non-audit staging only',
};

interface ExecutiveOverviewProps {
  // One independent request state per section, loaded by useWesOverview.
  sections: WesOverviewSections;
  hierarchy: WesHierarchyLevel;
  timeframe: WesTimeGrain;
  // Owned by the dashboard so it persists across tabs and clears when the report is left.
  scope: WesStagingScope;
  onScopeChange: (scope: WesStagingScope) => void;
}

// The Staging Scope never touches Total Pallets or the table.
const ExecutiveOverview: React.FC<ExecutiveOverviewProps> = ({ sections, hierarchy, timeframe, scope, onScopeChange }) => {
  const { overview, distribution, geography, trend } = sections;
  const { width } = useWindowDimensions();
  // Distribution and geography sit side by side from 992 wide, stacked below.
  const sideBySide = width >= 992;
  const geoBars = geography.data ? toGeoBars(geography.data, scope) : [];
  const trendModel = trend.data
    ? toTrend(trend.data, scope, hierarchy === 'location' ? WES_TREND_LOCATION_CAP : undefined)
    : { buckets: [], series: [], shown: 0, available: 0 };
  return (
    <View style={styles.container} testID="wes-executive-overview">
      <View style={styles.scopeBar}>
        <Text style={styles.scopeLabel}>Staging Scope</Text>
        <View style={styles.segmented} accessibilityRole="radiogroup">
          {SCOPES.map((key, index) => {
            const isActive = key === scope;
            return (
              <WesPressable
                key={key}
                style={[styles.segment, index > 0 && styles.segmentDivider, isActive && styles.segmentActive]}
                onPress={() => onScopeChange(key)}
                accessibilityState={{ selected: isActive }}
                testID={`wes-scope-${key}`}
              >
                {({ hovered }) => (
                  <Text style={[styles.segmentText, isActive && styles.segmentTextActive, hovered && !isActive && styles.segmentTextHover]}>{WES_SCOPE_LABEL[key]}</Text>
                )}
              </WesPressable>
            );
          })}
        </View>
        <Text style={styles.scopeNote} testID="wes-scope-note">{`${SCOPE_NOTE[scope]} · Total Pallets is unaffected`}</Text>
      </View>
      <WesKpiCards overview={overview.data} scope={scope} status={toSectionStatus(overview)} />

      <View style={[styles.chartRow, !sideBySide && styles.chartRowStacked]}>
        <WesDistributionCard data={distribution.data} scope={scope} status={toSectionStatus(distribution)} />
        <WesGeographyBars groups={geoBars} level={hierarchy} scope={scope} status={toSectionStatus(geography)} />
      </View>

      <WesTrendChart trend={trendModel} level={hierarchy} grain={timeframe} scope={scope} shown={trendModel.shown} available={trendModel.available} status={toSectionStatus(trend)} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { rowGap: 0 },
  scopeBar: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: 14, rowGap: 8, marginTop: 4, marginBottom: 16 },
  scopeLabel: { fontSize: 12, fontFamily: FontFamily.bold, color: '#616161', textTransform: 'uppercase', letterSpacing: 0.6 },
  segmented: { flexDirection: 'row', borderWidth: 1, borderColor: '#D0D0D0', borderRadius: 999, overflow: 'hidden', backgroundColor: '#FFFFFF' },
  segment: { paddingHorizontal: 22, paddingVertical: 9, backgroundColor: 'transparent', borderRadius: 999 },
  segmentDivider: {},
  segmentActive: { backgroundColor: '#034895' },
  segmentText: { fontSize: 14, fontFamily: FontFamily.medium, color: '#616161' },
  segmentTextActive: { color: '#FFFFFF', fontFamily: FontFamily.bold },
  segmentTextHover: { color: '#005CBC' },
  scopeNote: { fontSize: 12.5, fontFamily: FontFamily.regular, color: '#838383', flexShrink: 1 },
  chartRow: { flexDirection: 'row', columnGap: 20, rowGap: 20, alignItems: 'stretch', marginTop: 16, marginBottom: 20 },
  chartRowStacked: { flexDirection: 'column' },
});

export default ExecutiveOverview;
